import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import AxiosUrl from '../../AxiosUrl';
import card from '../HomeLayout/HomeLayout.module.css';
import styles from './HomeSections.module.css';

/**
 * Online Store → Home page sections (domains.homeSections): show or hide each product row
 * on the full-store home page. Every row is on unless switched off. Which products go in a
 * row is still set on the product (e.g. "Hot deal: Yes"); this only shows/hides the row.
 */
const SECTIONS = [
    { key: 'hotDeals', title: 'Hot Deals', text: 'Products set to "Hot deal: Yes".' },
    { key: 'trending', title: 'Trending Products', text: 'Products set to "Trending now: Yes".' },
    { key: 'newArrivals', title: 'New Arrivals', text: 'Your newest products, added automatically.' },
    { key: 'categoryRows', title: 'Category product rows', text: 'One row for each category you put on the home page.' },
    { key: 'topSelling', title: 'Top Selling', text: 'Your best sellers from the last 30 days.' },
];

const readSaved = (storeData) => {
    const hs = storeData?.homeSections || {};
    return SECTIONS.reduce((o, s) => ({ ...o, [s.key]: hs[s.key] !== false }), {});
};

function storeUrl(storeData) {
    if (!storeData) return '';
    if (storeData.domain) return `${storeData.DomainSSL === false ? 'http' : 'https'}://${storeData.domain}`;
    if (storeData.SubDomain) return `https://${storeData.SubDomain}`;
    return '';
}

function HomeSections({ storeData, uid, onSaved }) {
    const saved = useMemo(() => readSaved(storeData), [storeData]);
    const [on, setOn] = useState(saved);
    const [saving, setSaving] = useState(false);

    useEffect(() => setOn(saved), [saved]);

    // A single-product landing page has none of these rows.
    if (storeData?.homeLayout?.mode === 'single') return null;

    const dirty = SECTIONS.some((s) => on[s.key] !== saved[s.key]);
    const base = storeUrl(storeData);

    const save = async () => {
        try {
            setSaving(true);
            const res = await AxiosUrl.post(`/update-domain-new/${uid}`, { homeSections: on });
            if (res.data && res.data.error) throw new Error(res.data.error);
            toast.success('Home page sections saved');
            if (onSaved) onSaved();
        } catch (err) {
            const d = err?.response?.data;
            toast.error((typeof d === 'string' && d) || d?.error || err.message || 'Could not save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={card.card}>
            <div className={card.head}>
                <h2 className={card.title}>Home page sections</h2>
                <p className={card.sub}>Choose which product rows show on your home page.</p>
            </div>

            <div className={styles.list}>
                {SECTIONS.map((s) => (
                    <label key={s.key} className={styles.row}>
                        <span className={styles.text}>
                            <span className={styles.name}>{s.title}</span>
                            <span className={styles.desc}>{s.text}</span>
                        </span>
                        <span className={styles.state}>{on[s.key] ? 'Shown' : 'Hidden'}</span>
                        <input
                            type="checkbox"
                            className={styles.switchInput}
                            checked={on[s.key]}
                            onChange={(e) => setOn((o) => ({ ...o, [s.key]: e.target.checked }))}
                        />
                        <span className={styles.switch} aria-hidden="true" />
                    </label>
                ))}
            </div>

            <p className={card.hint}>
                A row also hides by itself when it has no products. Hiding a row does not change your products.
            </p>

            <div className={card.actions}>
                {base && (
                    <a className={card.btnSecondary} href={`${base}/`} target="_blank" rel="noopener noreferrer">
                        View store
                    </a>
                )}
                <button type="button" className={card.btnPrimary} onClick={save} disabled={!dirty || saving}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </div>
    );
}

export default HomeSections;
